import { dayjs } from "@/core/utils/date";
import type { IAiSpreadsheetSheet } from "@/payroll/ai-script/types";
import {
  SHEET_APPROVAL_TYPE,
  type IPayrollSheetApprovalAccessKey,
  type IPayrollSheetApprovalStatus,
  type ISheetApprovalStatus,
  type SheetApprovalType,
} from "@/payroll/payroll.d";

/**
 * The approval unit a sheet belongs to, or undefined when the sheet isn't under
 * approval at all. Sheets carry the key STRING while actions are posted against
 * the access key's id, so matching happens here once rather than at each caller.
 */
export const getSheetApproval = (
  sheet: Pick<IAiSpreadsheetSheet, "access_key"> | undefined,
  approvalStatus?: IPayrollSheetApprovalStatus | null,
): IPayrollSheetApprovalAccessKey | undefined => {
  if (!sheet?.access_key) return undefined;

  return approvalStatus?.access_keys.find(
    (accessKey) => accessKey.key === sheet.access_key,
  );
};

/** Never use group approval as a sheet's state in sheet mode. */
export const getSheetApprovalSubject = (
  accessKey: IPayrollSheetApprovalAccessKey,
  sheetId?: string,
) =>
  accessKey.approval_mode === "sheet"
    ? accessKey.sheets?.find((sheet) => sheet.id === sheetId)
    : accessKey;

/**
 * Derived from the units themselves rather than read off separate totals: the
 * list is already requester-filtered, so counting it here cannot disagree with
 * what the panel below is showing.
 */
export const getApprovalCoverage = (
  approvalStatus?: IPayrollSheetApprovalStatus | null,
) => {
  const units = approvalStatus?.access_keys ?? [];
  const met = (gate: SheetApprovalType) =>
    units.filter((unit) => unit.approvals[gate].approved).length;

  return {
    total: units.length,
    approvedByType: {
      [SHEET_APPROVAL_TYPE.readyForTechApproval]: met(
        SHEET_APPROVAL_TYPE.readyForTechApproval,
      ),
      [SHEET_APPROVAL_TYPE.payrollFinalized]: met(
        SHEET_APPROVAL_TYPE.payrollFinalized,
      ),
    } satisfies Record<SheetApprovalType, number>,
  };
};

export const getMissingTechApprovalAccessKeys = (
  approvalStatus?: IPayrollSheetApprovalStatus | null,
): IPayrollSheetApprovalAccessKey[] =>
  approvalStatus?.access_keys.filter(
    (accessKey) =>
      !accessKey.approvals[SHEET_APPROVAL_TYPE.readyForTechApproval].approved,
  ) ?? [];

/**
 * A one-line stand-in for a unit's sheet list. Statement units routinely hold a
 * sheet per technician, so the full list would be a paragraph — name the first
 * couple and count the rest.
 */
export const summarizeSheetNames = (sheetNames: string[]) => {
  if (sheetNames.length <= 3) return sheetNames.join(", ");

  return `${sheetNames.slice(0, 2).join(", ")} +${sheetNames.length - 2} more`;
};

export const APPROVAL_INTENT = {
  approve: "approve",
  confirmRemoval: "confirm_removal",
} as const;

export type ApprovalIntent =
  (typeof APPROVAL_INTENT)[keyof typeof APPROVAL_INTENT];

/**
 * The two directions are deliberately not symmetric. Giving an approval is the
 * reviewer saying what they already decided, so it submits on the click. Taking
 * one back invalidates work other people may have acted on, and the action
 * record can carry a reason — so it stops for a confirmation first.
 */
export const resolveApprovalIntent = (enabled: boolean): ApprovalIntent =>
  enabled ? APPROVAL_INTENT.approve : APPROVAL_INTENT.confirmRemoval;

/**
 * The sheet line under a unit's name, or null when it would only repeat that
 * name — a one-sheet unit is usually called after its sheet, and echoing it
 * turns every such row into a stutter.
 */
export const getSheetSummary = (accessKey: IPayrollSheetApprovalAccessKey) => {
  const summary = summarizeSheetNames(accessKey.sheet_names);

  return summary === accessKey.name ? null : summary;
};

/**
 * A reason reads as its own sentence after the line that names the action. It
 * arrives as free text a person typed, so it may or may not already end in
 * punctuation.
 */
const formatReason = (reason: string | null): string => {
  const trimmed = reason?.trim();
  if (!trimmed) return "";
  return /[.!?]$/.test(trimmed) ? `. ${trimmed}` : `. ${trimmed}.`;
};

/**
 * Who settled a gate, and when, in one short line — or null while nobody has.
 *
 * The system phrasing is deliberately different from a person's: a rerun
 * withdrawing a sign-off is not somebody's decision, and reading it as one sends
 * the reader to ask a colleague what they did. `reason` is only carried by a
 * removal, so it appears only where it explains one.
 */
export const describeApprovalProvenance = (
  status: ISheetApprovalStatus,
): string | null => {
  if (!status.at) return null;

  const when = dayjs(status.at).fromNow();

  if (status.inherited_from_group && status.approved) {
    return `Inherited group approval by ${status.actor_name ?? "someone"} ${when}`;
  }

  if (status.source === "system") {
    return status.approved
      ? `Approved automatically ${when}`
      : `Withdrawn automatically ${when}${formatReason(status.reason)}`;
  }

  const who = status.actor_name ?? "someone";
  return status.approved
    ? `Approved by ${who} ${when}`
    : `Removed by ${who} ${when}${formatReason(status.reason)}`;
};
